/* ==========================================================================
   ODINS — Quick add sheet (shared)
   assets/odins-quick-add.js

   One quick-add sheet for any section. A section adds its bag buttons and
   renders snippets/odins-quick-add.liquid once; that snippet loads this
   file and the stylesheet, and writes the store's details (money format,
   ring settings, colours) into the page.

   A bag button is any element with data-odn-qa and data-handle, plus
   optionally:
     data-variant-id      open on this variant
     data-label-add       the Add to cart button's text
     data-label-sold-out  its text when the chosen option is sold out
     data-ring-sizes      the product's ring sizes metafield, as entered —
                          "5 - 23" for a range or "6, 8, 10, 12" for a list

   The sheet shows the product's options, a ring size dropdown and the
   ring size guide for products carrying the ring tag, and Add to cart.
   Adding opens the cart drawer.

   Several sections may render the snippet, so this file can arrive more
   than once on a page; it sets itself up only the first time.
   ========================================================================== */

(function () {
  'use strict';

  if (window.OdnQuickAdd) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var config = null;

  function readConfig() {
    if (config) return config;
    var el = document.querySelector('script[data-odn-qa-config]');
    try { config = el ? JSON.parse(el.textContent) : {}; } catch (err) { config = {}; }
    return config;
  }

  /* ----------------------------------------------------------------------
     Money, in the store's own format
     ---------------------------------------------------------------------- */
  function formatMoney(cents) {
    var format = readConfig().moneyFormat || '';
    function group(n, dp, thou, dec) {
      var parts = (n / 100).toFixed(dp).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thou);
      return parts.join(dec);
    }
    if (!format) return group(cents, 2, ',', '.');
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
      switch (key) {
        case 'amount_no_decimals': return group(cents, 0, ',', '.');
        case 'amount_with_comma_separator': return group(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(cents, 0, '.', ',');
        default: return group(cents, 2, ',', '.');
      }
    });
  }

  function sized(src, w) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function rootUrl() {
    return (readConfig().root || '/').replace(/\/?$/, '/');
  }

  /* Ring sizes, read the same way as the product page's selector:
       "5 - 23", "5-23", "5 – 23", "5 to 23"   every size, both ends included
       "6, 8, 10, 12"                           exactly these, in this order
       "8"                                      just that size
     Anything unreadable gives an empty list, and the caller falls back. */
  function parseSizes(raw) {
    var s = String(raw || '').toLowerCase().replace(/[\u2013\u2014]/g, '-').replace(/\bto\b/g, '-').trim();
    if (!s) return [];
    var isNum = function (x) { return /^\d+(\.\d+)?$/.test(x); };

    if (s.indexOf(',') > -1) {
      return s.split(',').map(function (x) { return x.trim(); }).filter(isNum);
    }
    var m = s.match(/^(\d+)\s*-\s*(\d+)$/);
    if (m) {
      var lo = parseInt(m[1], 10), hi = parseInt(m[2], 10);
      if (hi < lo) { var t = lo; lo = hi; hi = t; }
      var out = [];
      for (var n = lo; n <= hi; n++) out.push(String(n));
      return out;
    }
    return isNum(s) ? [s] : [];
  }

  function ringSizes(opener) {
    var own = parseSizes(opener && opener.getAttribute('data-ring-sizes'));
    if (own.length) return own;
    var fallback = parseSizes(readConfig().ringSizes);
    return fallback.length ? fallback : parseSizes('5-23');
  }

  /* ----------------------------------------------------------------------
     The sheet
     ---------------------------------------------------------------------- */
  var sheet = null;
  var state = null;
  var requestNo = 0;

  function q(sel) { return sheet.querySelector(sel); }

  function build() {
    if (sheet) return;
    var c = readConfig();
    sheet = document.createElement('dialog');
    sheet.className = 'odn-qa-sheet';
    sheet.setAttribute('aria-labelledby', 'OdnQaTitle');
    /* Brand colours from the store's cart settings, so the sheet matches
       the drawer it hands over to. */
    sheet.style.setProperty('--qa-ink', c.ink || '#101F3C');
    sheet.style.setProperty('--qa-muted', c.muted || '#5C6883');
    sheet.style.setProperty('--qa-rule', c.rule || '#E4E8F0');
    sheet.style.setProperty('--qa-tint', c.tint || '#F4F6FA');

    sheet.innerHTML =
      '<button type="button" class="odn-qa-sheet__close" data-qa-close aria-label="Close">' +
        '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke-linecap="round"/></svg>' +
      '</button>' +
      '<div class="odn-qa-sheet__panel">' +
        '<p class="odn-qa-sheet__loading">Loading</p>' +
        '<div class="odn-qa-sheet__body">' +
          '<div data-qa-main>' +
          '<div class="odn-qa-sheet__head">' +
            '<img class="odn-qa-sheet__img" data-qa-img alt="">' +
            '<div>' +
              '<h2 class="odn-qa-sheet__title" id="OdnQaTitle" data-qa-title></h2>' +
              '<p class="odn-qa-sheet__price" data-qa-price></p>' +
            '</div>' +
          '</div>' +
          '<div data-qa-options></div>' +
          /* A scrollable dropdown, like the product page's ring size
             selector, rather than the browser's own list — which on a
             phone opens as a full-screen list of every size. It opens in
             place, so it can't be cut off by the sheet's edges. */
          '<div class="odn-qa-sheet__group" data-qa-ring hidden>' +
            '<div class="odn-qa-sheet__labelrow">' +
              '<span class="odn-qa-sheet__label" id="OdnQaRingLabel"></span>' +
              '<button type="button" class="odn-qa-sheet__sglink" data-qa-sg-open hidden>' +
                '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M2 8.5h20v7H2z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M6 8.5v3M10 8.5v4.5M14 8.5v3M18 8.5v4.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
                'Size guide' +
              '</button>' +
            '</div>' +
            '<div class="odn-qa-rs" data-qa-rs data-open="false">' +
              '<button type="button" class="odn-qa-rs__trigger" data-qa-rs-trigger role="combobox" aria-haspopup="listbox" aria-expanded="false" aria-controls="OdnQaRingList" aria-labelledby="OdnQaRingLabel">' +
                '<span data-qa-rs-value></span>' +
                '<svg width="12" height="8" viewBox="0 0 12 8" aria-hidden="true"><path d="M1 1L6 6L11 1" fill="none" stroke="currentColor" stroke-width="2"/></svg>' +
              '</button>' +
              '<ul class="odn-qa-rs__list" id="OdnQaRingList" role="listbox" aria-labelledby="OdnQaRingLabel" tabindex="-1" data-qa-rs-list></ul>' +
            '</div>' +
          '</div>' +
          '<p class="odn-qa-sheet__error" role="alert" data-qa-error hidden></p>' +
          '<div class="odn-qa-sheet__actions">' +
            '<button type="button" class="odn-qa-sheet__btn" data-qa-add></button>' +
          '</div>' +
          '<a class="odn-qa-sheet__more" data-qa-more href="#">View full details</a>' +
          '</div>' +
          /* The ring size guide, shown in place of the sheet's contents
             rather than as a second popup on top of the first. */
          '<div class="odn-qa-sg" data-qa-guide hidden>' +
            '<button type="button" class="odn-qa-sg__back" data-qa-sg-back>' +
              '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
              'Back to sizes' +
            '</button>' +
            '<h3 class="odn-qa-sg__title" id="OdnQaSgTitle">Ring size guide</h3>' +
            '<p class="odn-qa-sg__note" data-qa-sg-note></p>' +
            '<div data-qa-sg-chart></div>' +
            '<a class="odn-qa-sg__wa" data-qa-sg-wa target="_blank" rel="noopener" hidden>' +
              '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 00-8.6 15.1L2 22l5-1.3A10 10 0 1012 2zm5.8 14.2c-.2.7-1.4 1.3-2 1.4-.5.1-1.1.1-1.8-.1-.4-.1-1-.3-1.7-.6-3-1.3-4.9-4.3-5-4.5-.2-.2-1.2-1.6-1.2-3s.7-2.1 1-2.4c.3-.3.6-.4.8-.4h.6c.2 0 .4 0 .7.5l.9 2.1c.1.2.1.4 0 .6l-.4.5-.3.4c-.1.1-.3.3-.1.6.2.3.7 1.2 1.6 2 1.1.9 2 1.2 2.3 1.4.3.1.4.1.6-.1l.9-1c.2-.2.4-.2.6-.1l2 1c.3.1.5.2.5.3.1.2.1.7-.1 1.4z"/></svg>' +
              '<span data-qa-sg-wa-label></span>' +
            '</a>' +
          '</div>' +
        '</div>' +
      '</div>';

    document.body.appendChild(sheet);

    q('[data-qa-close]').addEventListener('click', close);
    sheet.addEventListener('click', function (e) { if (e.target === sheet) close(); });
    sheet.addEventListener('close', function () {
      document.documentElement.style.overflow = '';
      if (state && state.opener && state.opener.focus && document.contains(state.opener)) state.opener.focus();
    });
    q('[data-qa-add]').addEventListener('click', add);
    wireRingSize();
    wireGuide();
  }

  /* ----------------------------------------------------------------------
     Ring size guide
     The chart comes from the page (snippets/odins-ring-size-chart.liquid),
     the same one the product page's size guide shows. Opening it swaps the
     sheet's contents; the size chosen is highlighted, and tapping a row the
     ring comes in chooses that size and goes back.
     ---------------------------------------------------------------------- */
  function guideIsOpen() { return !q('[data-qa-guide]').hidden; }

  function wireGuide() {
    var tpl = document.querySelector('template[data-odn-qa-sizeguide]');
    if (!tpl) return;
    q('[data-qa-sg-chart]').appendChild(document.importNode(tpl.content, true));
    q('[data-qa-sg-open]').hidden = false;
    q('[data-qa-sg-open]').addEventListener('click', openGuide);
    q('[data-qa-sg-back]').addEventListener('click', function () { closeGuide(true); });

    var chart = q('[data-qa-sg-chart]');
    function choose(row) {
      if (!row || row.dataset.offered !== 'true') return;
      rsSelect(row.dataset.size);
      closeGuide(true);
    }
    chart.addEventListener('click', function (e) { choose(e.target.closest('tr[data-size]')); });
    chart.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var row = e.target.closest('tr[data-size]');
      if (!row) return;
      e.preventDefault();
      choose(row);
    });
  }

  function openGuide() {
    var offered = state.sizes || [];
    q('[data-qa-guide]').querySelectorAll('tr[data-size]').forEach(function (row) {
      var size = row.dataset.size;
      var isOffered = offered.indexOf(size) > -1;
      row.dataset.offered = isOffered ? 'true' : 'false';
      row.dataset.selected = size === state.ringValue ? 'true' : 'false';
      if (isOffered) {
        row.tabIndex = 0;
        row.setAttribute('aria-label', 'Choose size ' + size);
      } else {
        row.removeAttribute('tabindex');
        row.removeAttribute('aria-label');
      }
    });

    var note = q('[data-qa-sg-note]');
    var shownInChart = !!q('[data-qa-guide]').querySelector('tr[data-selected="true"]');
    note.textContent = 'Sizes this ring comes in are in bold — tap one to choose it.' +
      (state.ringValue && !shownInChart ? ' Size ' + state.ringValue + ' sits between the rows shown.' : '');

    var c = readConfig();
    var wa = q('[data-qa-sg-wa]');
    if (c.waNumber) {
      var text = (c.waMessage || 'Hi, I need help with my ring size') + ' — ' + (state.product ? state.product.title : '');
      wa.href = 'https://wa.me/' + String(c.waNumber).replace(/[^\d]/g, '') + '?text=' + encodeURIComponent(text);
      q('[data-qa-sg-wa-label]').textContent = c.waLabel || 'Ask us on WhatsApp';
      wa.hidden = false;
    } else {
      wa.hidden = true;
    }

    if (rsIsOpen()) rsClose(false);
    q('[data-qa-main]').hidden = true;
    q('[data-qa-guide]').hidden = false;
    q('.odn-qa-sheet__panel').scrollTop = 0;
    var sel = q('[data-qa-guide] tr[data-selected="true"]');
    if (sel) sel.scrollIntoView({ block: 'nearest' });
    q('[data-qa-sg-back]').focus();
  }

  function closeGuide(focusLink) {
    q('[data-qa-guide]').hidden = true;
    q('[data-qa-main]').hidden = false;
    q('.odn-qa-sheet__panel').scrollTop = 0;
    if (focusLink) q('[data-qa-sg-open]').focus();
  }

  /* ----------------------------------------------------------------------
     Ring size dropdown
     ---------------------------------------------------------------------- */
  var rsActive = -1;

  function rsOptions() { return Array.prototype.slice.call(q('[data-qa-rs-list]').querySelectorAll('[role="option"]')); }
  function rsIsOpen() { return q('[data-qa-rs]').dataset.open === 'true'; }

  function rsSetActive(i) {
    var opts = rsOptions();
    rsActive = i;
    opts.forEach(function (o, n) { o.dataset.active = n === i ? 'true' : 'false'; });
    var trigger = q('[data-qa-rs-trigger]');
    if (i > -1 && opts[i]) {
      opts[i].scrollIntoView({ block: 'nearest' });
      trigger.setAttribute('aria-activedescendant', opts[i].id);
    } else {
      trigger.removeAttribute('aria-activedescendant');
    }
  }

  function rsSelect(value) {
    state.ringValue = value;
    q('[data-qa-rs-value]').textContent = 'Size ' + value;
    rsOptions().forEach(function (o) { o.setAttribute('aria-selected', o.dataset.value === value ? 'true' : 'false'); });
  }

  function rsOpen() {
    q('[data-qa-rs]').dataset.open = 'true';
    q('[data-qa-rs-trigger]').setAttribute('aria-expanded', 'true');
    var opts = rsOptions();
    var i = opts.findIndex(function (o) { return o.dataset.value === state.ringValue; });
    rsSetActive(i > -1 ? i : 0);
  }

  function rsClose(focusTrigger) {
    q('[data-qa-rs]').dataset.open = 'false';
    q('[data-qa-rs-trigger]').setAttribute('aria-expanded', 'false');
    rsSetActive(-1);
    if (focusTrigger) q('[data-qa-rs-trigger]').focus();
  }

  function wireRingSize() {
    var box = q('[data-qa-rs]');
    q('[data-qa-rs-trigger]').addEventListener('click', function () { rsIsOpen() ? rsClose(false) : rsOpen(); });
    q('[data-qa-rs-list]').addEventListener('click', function (e) {
      var opt = e.target.closest('[role="option"]');
      if (!opt) return;
      rsSelect(opt.dataset.value);
      rsClose(true);
    });
    /* A tap anywhere else in the sheet closes the list. */
    sheet.addEventListener('click', function (e) {
      if (rsIsOpen() && !box.contains(e.target)) rsClose(false);
    });
    box.addEventListener('keydown', function (e) {
      var opts = rsOptions();
      if (!rsIsOpen()) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); rsOpen(); }
        return;
      }
      switch (e.key) {
        /* Escape closes the list first, not the whole sheet. */
        case 'Escape': e.preventDefault(); e.stopPropagation(); rsClose(true); break;
        case 'ArrowDown': e.preventDefault(); rsSetActive(Math.min(opts.length - 1, rsActive + 1)); break;
        case 'ArrowUp': e.preventDefault(); rsSetActive(Math.max(0, rsActive - 1)); break;
        case 'Home': e.preventDefault(); rsSetActive(0); break;
        case 'End': e.preventDefault(); rsSetActive(opts.length - 1); break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (rsActive > -1 && opts[rsActive]) { rsSelect(opts[rsActive].dataset.value); rsClose(true); }
          break;
        case 'Tab': rsClose(false); break;
      }
    });
    /* The dialog closes itself on Escape too; keep it open while the list is. */
    sheet.addEventListener('cancel', function (e) {
      if (rsIsOpen()) { e.preventDefault(); rsClose(true); return; }
      if (guideIsOpen()) { e.preventDefault(); closeGuide(true); }
    });
  }

  function close() {
    if (sheet && sheet.open) sheet.close();
  }

  /* Clear what the previous product left behind and show this card's own
     photo at once. The sheet is reused, and an image keeps its old picture
     until a new one loads — which would flash the last product first. */
  function reset(opener) {
    q('[data-qa-title]').textContent = '';
    q('[data-qa-price]').textContent = '';
    q('[data-qa-options]').textContent = '';
    q('[data-qa-ring]').hidden = true;
    q('[data-qa-rs-list]').textContent = '';
    if (rsIsOpen()) rsClose(false);
    if (guideIsOpen()) closeGuide(false);
    showError('');
    var img = q('[data-qa-img]');
    var card = opener && opener.closest ? (opener.closest('li') || opener.parentElement) : null;
    var shot = card ? card.querySelector('img') : null;
    if (shot && (shot.currentSrc || shot.src)) img.src = shot.currentSrc || shot.src;
    else img.removeAttribute('src');
  }

  function open(opener) {
    var handle = opener.getAttribute('data-handle');
    if (!handle) return;
    build();
    var mine = ++requestNo;
    state = {
      opener: opener,
      product: null,
      options: [],
      ring: false,
      ringValue: '',
      sizes: [],
      labelAdd: opener.getAttribute('data-label-add') || 'Add to cart',
      labelSoldOut: opener.getAttribute('data-label-sold-out') || 'Sold out'
    };
    reset(opener);
    sheet.dataset.loading = 'true';
    if (typeof sheet.showModal === 'function') sheet.showModal();
    else sheet.setAttribute('open', '');
    document.documentElement.style.overflow = 'hidden';

    fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (product) {
        /* Tap one product, then another quickly, and the first answer can
           arrive last. Only the most recent request may fill the sheet. */
        if (mine !== requestNo) return;
        state.product = product;
        render(product, opener.getAttribute('data-variant-id'));
        sheet.dataset.loading = 'false';
      })
      .catch(function () {
        if (mine !== requestNo) return;
        /* The product page can do everything the sheet does. */
        window.location.href = rootUrl() + 'products/' + handle;
      });
  }

  function optionNames(p) {
    return (p.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
  }

  function render(product, variantId) {
    var start = null;
    product.variants.forEach(function (v) { if (String(v.id) === String(variantId)) start = v; });
    if (!start) product.variants.forEach(function (v) { if (!start && v.available) start = v; });
    if (!start) start = product.variants[0];

    q('[data-qa-title]').textContent = product.title;

    var names = optionNames(product);
    state.options = names.map(function (name, i) {
      var values = [];
      product.variants.forEach(function (v) {
        if (values.indexOf(v.options[i]) < 0) values.push(v.options[i]);
      });
      return { name: name, index: i, values: values, selected: start ? start.options[i] : values[0] };
    }).filter(function (o) {
      /* Shopify's placeholder for a product with no options. */
      return !(o.values.length === 1 && o.values[0] === 'Default Title');
    });

    var wrap = q('[data-qa-options]');
    wrap.textContent = '';
    state.options.forEach(function (opt) {
      var group = document.createElement('fieldset');
      group.className = 'odn-qa-sheet__group';
      var legend = document.createElement('legend');
      legend.className = 'odn-qa-sheet__label';
      legend.appendChild(document.createTextNode(opt.name + ': '));
      var chosen = document.createElement('strong');
      chosen.textContent = opt.selected;
      legend.appendChild(chosen);
      group.appendChild(legend);

      var pills = document.createElement('div');
      pills.className = 'odn-qa-sheet__pills';
      pills.setAttribute('role', 'radiogroup');
      opt.values.forEach(function (val) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'odn-qa-sheet__pill';
        b.setAttribute('role', 'radio');
        b.textContent = val;
        b.dataset.value = val;
        b.addEventListener('click', function () {
          opt.selected = val;
          chosen.textContent = val;
          refresh();
        });
        pills.appendChild(b);
      });
      group.appendChild(pills);
      opt.el = pills;
      wrap.appendChild(group);
    });

    /* Rings get a size picker, written to the same line property as the
       product page, so the size reaches the order either way. */
    var c = readConfig();
    var ringTag = String(c.ringTag || '').toLowerCase();
    var tags = (product.tags || []).map(function (t) { return String(t).toLowerCase().trim(); });
    state.ring = !!ringTag && tags.indexOf(ringTag) > -1;
    var ringBox = q('[data-qa-ring]');
    ringBox.hidden = !state.ring;
    if (state.ring) {
      q('#OdnQaRingLabel').textContent = c.ringProperty || 'Ring Size';
      var list = q('[data-qa-rs-list]');
      list.textContent = '';
      var sizes = ringSizes(state.opener);
      state.sizes = sizes;
      sizes.forEach(function (n, i) {
        var li = document.createElement('li');
        li.className = 'odn-qa-rs__option';
        li.setAttribute('role', 'option');
        li.id = 'OdnQaRingOpt-' + i;
        li.dataset.value = n;
        li.setAttribute('aria-selected', 'false');
        li.textContent = 'Size ' + n;
        list.appendChild(li);
      });
      /* The middle of the product's own list — for "6, 8, 10, 12" that is
         8, a size the ring actually comes in — matching the product page. */
      rsSelect(sizes[Math.floor((sizes.length - 1) / 2)]);
    }

    refresh();
  }

  function currentVariant() {
    var p = state && state.product;
    if (!p) return null;
    if (!state.options.length) return p.variants[0];
    var match = null;
    p.variants.forEach(function (v) {
      if (match) return;
      var ok = state.options.every(function (opt) { return v.options[opt.index] === opt.selected; });
      if (ok) match = v;
    });
    return match;
  }

  function refresh() {
    var p = state.product;
    var v = currentVariant();

    /* A value is struck through when no in-stock variant pairs it with
       what is chosen in the other options. */
    state.options.forEach(function (opt) {
      opt.el.querySelectorAll('.odn-qa-sheet__pill').forEach(function (b) {
        var val = b.dataset.value;
        b.setAttribute('aria-checked', val === opt.selected ? 'true' : 'false');
        var possible = p.variants.some(function (x) {
          if (x.options[opt.index] !== val || !x.available) return false;
          return state.options.every(function (other) {
            return other === opt || x.options[other.index] === other.selected;
          });
        });
        b.dataset.unavailable = possible ? 'false' : 'true';
      });
    });

    var price = q('[data-qa-price]');
    price.textContent = '';
    if (v) {
      var now = document.createElement('span');
      now.textContent = formatMoney(v.price);
      price.appendChild(now);
      if (v.compare_at_price && v.compare_at_price > v.price) {
        var was = document.createElement('s');
        was.textContent = formatMoney(v.compare_at_price);
        price.appendChild(was);
      }
    }

    var src = (v && v.featured_image && v.featured_image.src) || p.featured_image;
    if (src) q('[data-qa-img]').src = sized(src, 300);

    var ok = !!(v && v.available);
    var btn = q('[data-qa-add]');
    btn.disabled = !ok;
    btn.textContent = ok ? state.labelAdd : state.labelSoldOut;
    q('[data-qa-more]').href = p.url + (v ? '?variant=' + v.id : '');
  }

  function showError(msg) {
    var el = q('[data-qa-error]');
    el.textContent = msg || '';
    el.hidden = !msg;
  }

  function add() {
    var v = currentVariant();
    if (!v || !v.available) return;
    var btn = q('[data-qa-add]');
    if (btn.dataset.busy === 'true') return;
    btn.dataset.busy = 'true';
    showError('');

    var item = { id: v.id, quantity: 1 };
    if (state.ring) {
      item.properties = {};
      item.properties[readConfig().ringProperty || 'Ring Size'] = state.ringValue;
    }

    var drawer = document.querySelector('cart-drawer');
    var body = { items: [item] };
    if (drawer && drawer.getSectionsToRender) {
      body.sections = drawer.getSectionsToRender().map(function (x) { return x.id; });
      body.sections_url = window.location.pathname;
    }

    fetch((readConfig().cartAdd || '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        btn.dataset.busy = 'false';
        if (json.status) {
          showError(json.description || json.message || 'This piece could not be added.');
          return;
        }
        close();
        if (drawer && drawer.renderContents) {
          /* An empty cart marks the drawer as empty, which hides its
             header, items and total. Dawn's own Add to cart lifts that
             mark first; without it the drawer opens blank, with no way to
             close it. */
          drawer.classList.remove('is-empty');
          drawer.renderContents(json);
        } else {
          window.location.href = (readConfig().cartAdd || '/cart/add').replace('/add', '');
        }
      })
      .catch(function () {
        btn.dataset.busy = 'false';
        showError('Something went wrong adding this piece. Please try again.');
      });
  }

  /* ----------------------------------------------------------------------
     Bag buttons, wherever they are — including cards that arrive later,
     like recommendations loaded after the page.
     ---------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest ? e.target.closest('[data-odn-qa]') : null;
    if (!b) return;
    e.preventDefault();
    open(b);
  });

  window.OdnQuickAdd = { open: open, close: close, parseSizes: parseSizes, reducedMotion: REDUCED };
})();